import React from 'react';
import { Link } from 'react-router-dom';
import OfficialCitation from '../explore/OfficialCitation';
import { LAST_VERIFIED } from '../../data/officialSources';
import {
	confirmStep, isStepConfirmed, setStepStatus, setActiveStep,
} from '../../data/journeys/journeyProgress';

function stepConfirmSummary(guided) {
	const items = guided.taskChecklist || [];
	if (items.length === 0) return null;
	return items.map((item) => {
		if (item.summary) return item.summary;
		if (item.type === 'choice' && item.options?.length) {
			return `${item.label}: ${item.options.map((o) => o.label).join(', ')}`;
		}
		return item.label;
	});
}

function ActionCard({ action }) {
	return (
		<article className="guided-action-card">
			<h4 className="guided-action-card__title">{action.title}</h4>
			{action.description && <p className="guided-action-card__desc">{action.description}</p>}
			{action.address && (
				<div className="guided-action-card__row">
					<strong>Address</strong>
					<span>{action.address}</span>
				</div>
			)}
			{action.mapsUrl && (
				<a href={action.mapsUrl} target="_blank" rel="noopener noreferrer" className="guided-action-card__maps">
					Open Google Maps →
				</a>
			)}
			{action.hours && (
				<div className="guided-action-card__row">
					<strong>Hours</strong>
					<span>{action.hours}</span>
				</div>
			)}
			{action.bring && action.bring.length > 0 && (
				<div className="guided-action-card__bring">
					<strong>Take with you:</strong>
					<ul>
						{action.bring.map((item) => <li key={item}>{item}</li>)}
					</ul>
				</div>
			)}
			<div className="guided-action-card__meta">
				{action.fee && <span><strong>Fee:</strong> {action.fee}</span>}
				{action.waitTime && <span><strong>Wait:</strong> {action.waitTime}</span>}
				{action.processingTime && <span><strong>Processing:</strong> {action.processingTime}</span>}
			</div>
			{action.mistakes && action.mistakes.length > 0 && (
				<div className="guided-action-card__mistakes">
					<strong>Common mistakes</strong>
					<ul>
						{action.mistakes.map((m) => <li key={m}>{m}</li>)}
					</ul>
				</div>
			)}
			{action.officialUrl && (
				<a href={action.officialUrl} target="_blank" rel="noopener noreferrer" className="guided-action-card__official">
					{action.officialLabel || 'Official website'} →
				</a>
			)}
		</article>
	);
}

function StepConfirm({ slug, scope, guided, progress, stepIndex, totalSteps, onProgressUpdate, onNext }) {
	const bullets = stepConfirmSummary(guided);
	const confirmed = isStepConfirmed(progress, scope);
	const isLast = stepIndex >= totalSteps - 1;

	function handleConfirm() {
		let updated = confirmStep(slug, scope);
		updated = setStepStatus(slug, scope, 'complete');
		onProgressUpdate(updated);
		if (!isLast && onNext) onNext();
	}

	return (
		<div className={`guided-step-confirm${confirmed ? ' is-done' : ''}`}>
			<p className="guided-step-confirm__lead">
				{confirmed
					? 'Step confirmed — you can move on or review the reference detail below.'
					: 'Read the guidance above, then confirm when you are ready.'}
			</p>
			{bullets && bullets.length > 0 && !confirmed && (
				<ul className="guided-step-confirm__bullets">
					{bullets.map((line) => <li key={line}>{line}</li>)}
				</ul>
			)}
			<button
				type="button"
				className="guided-step-confirm__btn"
				onClick={handleConfirm}
			>
				{confirmed
					? '✓ Confirmed'
					: isLast
						? "I've got this — finish journey"
						: "I've got this — next step →"}
			</button>
		</div>
	);
}

function GuidedStepPanel({
	journey, step, stepIndex, totalSteps, progress, onProgressUpdate, onNext,
}) {
	const guided = step.guided || {};
	const scope = step.id;
	const slug = journey.slug;
	const hasTasks = (guided.taskChecklist || []).length > 0;

	return (
		<div className="guided-step-panel" id={step.id}>
			<div className="guided-step-panel__header">
				<p className="guided-step-panel__eyebrow">Step {stepIndex + 1} of {totalSteps}</p>
				<h2 className="guided-step-panel__title">{step.icon} {step.name}</h2>
				{guided.objective && (
					<p className="guided-step-panel__objective"><strong>Objective:</strong> {guided.objective}</p>
				)}
			</div>

			{guided.why && (
				<div className="guided-step-panel__why">
					<strong>Why this matters</strong>
					<p>{guided.why}</p>
				</div>
			)}

			{guided.estimatedTime && (
				<p className="guided-step-panel__time">⏱ Estimated time: {guided.estimatedTime}</p>
			)}

			{guided.prerequisites && guided.prerequisites.length > 0 && (
				<p className="guided-step-panel__prereq">
					<strong>Prerequisites:</strong>{' '}
					{guided.prerequisites.map((id) => journey.steps.find((s) => s.id === id)?.name || id).join(', ')}
				</p>
			)}

			{guided.documentsRequired && guided.documentsRequired.length > 0 && (
				<div className="guided-step-panel__docs">
					<strong>Documents required</strong>
					<ul>
						{guided.documentsRequired.map((d) => <li key={d}>{d}</li>)}
					</ul>
				</div>
			)}

			{guided.actions && guided.actions.length > 0 && (
				<div className="guided-actions-row">
					{guided.actions.map((action) => (
						<ActionCard key={action.id} action={action} />
					))}
				</div>
			)}

			{guided.officeLocations && guided.officeLocations.length > 0 && (
				<div className="guided-actions-row">
					{guided.officeLocations.map((office) => (
					<article key={office.id} className="guided-office-card">
						<h4 className="guided-office-card__title">{office.name}</h4>
						{office.image && (
							<figure className="guided-office-card__figure">
								<img src={office.image.url} alt={office.name} loading="lazy" />
								<figcaption>{office.image.credit}</figcaption>
							</figure>
						)}
						<p><strong>Address:</strong> {office.address}</p>
						{office.phone && <p><strong>Phone:</strong> {office.phone}</p>}
						{office.hours && (
							<p><strong>Hours:</strong> {typeof office.hours === 'string' ? office.hours : office.hours.summary}</p>
						)}
						{office.hours && office.hours.closed && <p><small>Closed: {office.hours.closed}</small></p>}
						<a href={office.mapsUrl} target="_blank" rel="noopener noreferrer" className="guided-action-card__maps">Open Google Maps →</a>
						{office.whatToBring && (
							<ul className="guided-office-card__bring">
								{office.whatToBring.map((item) => <li key={item}>{item}</li>)}
							</ul>
						)}
					</article>
					))}
				</div>
			)}

			{step.id === 'kathmandu' && journey.gearRentalShops && (
				<div className="guided-gear-shops">
					<h4>Gear rental & purchase (Thamel)</h4>
					<div className="guided-actions-row">
						{journey.gearRentalShops.map((shop) => (
							<article key={shop.name} className="guided-gear-shop">
								<strong>{shop.name}</strong>
								<p>{shop.items} — {shop.rates}</p>
								<p><small>{shop.tip}</small></p>
								<a href={shop.mapsUrl} target="_blank" rel="noopener noreferrer">Google Maps →</a>
							</article>
						))}
					</div>
				</div>
			)}

			{guided.officialPdfs && guided.officialPdfs.length > 0 && (
				<div className="guided-step-panel__pdfs">
					<strong>Official links</strong>
					<ul>
						{guided.officialPdfs.map((pdf) => (
							<li key={pdf.url}>
								<a href={pdf.url} target="_blank" rel="noopener noreferrer">{pdf.label} →</a>
							</li>
						))}
					</ul>
				</div>
			)}

			{guided.alternatives && guided.alternatives.length > 0 && (
				<div className="guided-step-panel__alternatives">
					<strong>Alternatives</strong>
					<ul>
						{guided.alternatives.map((a) => <li key={a}>{a}</li>)}
					</ul>
				</div>
			)}

			{step.related && step.related.length > 0 && (
				<div className="guided-step-panel__related">
					{step.related.map((r) => (
						<Link key={r.to} to={r.to}>{r.label} →</Link>
					))}
				</div>
			)}

			{step.sections && step.sections.length > 0 && (
				<details className="guided-step-panel__reference">
					<summary>Full reference detail</summary>
					{step.sections.map((section) => (
						<div key={section.id} className="guided-step-panel__ref-block">
							<h5>{section.question}</h5>
							<p>{section.answer}</p>
							{section.source && (
								<OfficialCitation
									sourceName={section.source.name}
									sourceUrl={section.source.url}
									lastVerified={LAST_VERIFIED}
									subjectToChange
								/>
							)}
						</div>
					))}
				</details>
			)}

			{hasTasks ? (
				<StepConfirm
					slug={slug}
					scope={scope}
					guided={guided}
					progress={progress}
					stepIndex={stepIndex}
					totalSteps={totalSteps}
					onProgressUpdate={onProgressUpdate}
					onNext={onNext}
				/>
			) : (
				<div className="guided-step-panel__nav">
					{stepIndex < totalSteps - 1 && (
						<button
							type="button"
							className="guided-step-panel__btn guided-step-panel__btn--next"
							onClick={() => {
								let updated = setStepStatus(slug, step.id, 'in_progress');
								updated = setActiveStep(slug, journey.steps[stepIndex + 1].id);
								onProgressUpdate(updated);
								if (onNext) onNext();
							}}
						>
							<span className="guided-step-panel__btn-label">Continue to next step</span>
							<span className="guided-step-panel__btn-chevron" aria-hidden="true">→</span>
						</button>
					)}
				</div>
			)}
		</div>
	);
}

export default GuidedStepPanel;
